/**
 * TutorialSystem - Guides new players through the game
 */

import { eventBus, GameEvents } from '../core/EventBus.js';
import { gameState } from '../core/GameState.js';
import { STORAGE_KEYS } from '../core/Constants.js';

// Tutorial step definitions
const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    message: "Welcome to FishFishFish! Let's learn how to fish.",
    highlight: null,
    waitFor: null,
    autoAdvance: 3000 // Auto-advance after 3 seconds
  },
  {
    id: 'movement',
    message: "Click anywhere on the ground to walk there.",
    highlight: 'ground',
    waitFor: GameEvents.PLAYER_MOVED,
    tip: 'Try clicking near a pond!'
  },
  {
    id: 'find_pond',
    message: "Great! Now walk to a pond (the blue water) to start fishing.",
    highlight: 'pond',
    waitFor: 'near_pond',
    tip: 'Ponds are the circular blue areas'
  },
  {
    id: 'cast_line',
    message: "Click on the water to cast your line!",
    highlight: 'water',
    waitFor: GameEvents.FISHING_STARTED
  },
  {
    id: 'wait_bite',
    message: "Wait for a fish to bite... Watch the bobber!",
    highlight: 'float',
    waitFor: GameEvents.FISH_BITING,
    tip: 'The bobber will dip when a fish bites'
  },
  {
    id: 'minigame',
    message: "A fish is biting! Press SPACE (or tap) when the ball is in the green zone!",
    highlight: 'minigame',
    waitFor: GameEvents.MINIGAME_SUCCESS,
    tip: 'You have 3 chances - time it carefully!'
  },
  {
    id: 'caught',
    message: "Congratulations! You caught your first fish!",
    highlight: null,
    waitFor: null,
    autoAdvance: 2500
  },
  {
    id: 'inventory',
    message: "Press 'I' or tap the bag icon to see your fish collection.",
    highlight: 'inventory-button',
    waitFor: GameEvents.INVENTORY_OPENED,
    tip: 'Your collection shows all the fish you\'ve caught'
  },
  {
    id: 'complete',
    message: "Tutorial complete! Explore the world and catch them all!",
    highlight: null,
    waitFor: null,
    autoAdvance: 3000,
    onComplete: true
  }
];

class TutorialSystem {
  constructor() {
    this.currentStepIndex = 0;
    this.isActive = false;
    this.overlay = null;
    this.unsubscribers = [];

    // Check if tutorial was already completed
    this.completed = this.hasCompletedTutorial();
  }

  /**
   * Check if tutorial has been completed before
   */
  hasCompletedTutorial() {
    try {
      return localStorage.getItem(STORAGE_KEYS.TUTORIAL_COMPLETE) === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Mark tutorial as complete
   */
  markComplete() {
    try {
      localStorage.setItem(STORAGE_KEYS.TUTORIAL_COMPLETE, 'true');
      this.completed = true;
    } catch (error) {
      console.warn('Failed to save tutorial completion:', error);
    }
  }

  /**
   * Start the tutorial
   * @param {boolean} force - Force start even if completed before
   */
  start(force = false) {
    if (this.completed && !force) {
      console.log('Tutorial already completed');
      return;
    }

    this.isActive = true;
    this.currentStepIndex = 0;
    this.createOverlay();
    this.showStep(0);

    eventBus.emit(GameEvents.TUTORIAL_STEP, {
      step: 0,
      total: TUTORIAL_STEPS.length
    });
  }

  /**
   * Create the tutorial overlay
   */
  createOverlay() {
    // Remove existing overlay if any
    this.removeOverlay();

    this.overlay = document.createElement('div');
    this.overlay.id = 'tutorial-overlay';
    this.overlay.innerHTML = `
      <div class="tutorial-box">
        <div class="tutorial-header">
          <span class="tutorial-step-indicator">Step 1/${TUTORIAL_STEPS.length}</span>
          <button class="tutorial-skip-btn">Skip Tutorial</button>
        </div>
        <div class="tutorial-content">
          <div class="tutorial-icon">🎣</div>
          <div class="tutorial-text"></div>
          <div class="tutorial-tip"></div>
        </div>
        <div class="tutorial-footer">
          <button class="tutorial-next-btn">Continue</button>
        </div>
      </div>
      <div class="tutorial-pointer"></div>
    `;

    // Add styles
    const style = document.createElement('style');
    style.id = 'tutorial-styles';
    style.textContent = `
      #tutorial-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 10000;
      }

      .tutorial-box {
        position: absolute;
        bottom: 20%;
        left: 50%;
        transform: translateX(-50%);
        background: linear-gradient(135deg, #fff9e6 0%, #fff 100%);
        border: 4px solid #333;
        border-radius: 20px;
        padding: 20px;
        max-width: 400px;
        width: 90%;
        box-shadow: 0 8px 0 #333, 0 12px 20px rgba(0,0,0,0.3);
        pointer-events: auto;
        animation: tutorialBounce 0.4s ease-out;
      }

      @keyframes tutorialBounce {
        0% { transform: translateX(-50%) scale(0.8); opacity: 0; }
        60% { transform: translateX(-50%) scale(1.05); }
        100% { transform: translateX(-50%) scale(1); opacity: 1; }
      }

      .tutorial-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
        padding-bottom: 10px;
        border-bottom: 2px dashed #ddd;
      }

      .tutorial-step-indicator {
        font-family: 'Comic Sans MS', cursive;
        font-size: 14px;
        color: #666;
        font-weight: bold;
      }

      .tutorial-skip-btn {
        background: #ff6b6b;
        color: white;
        border: 3px solid #333;
        border-radius: 10px;
        padding: 5px 12px;
        font-family: 'Comic Sans MS', cursive;
        font-size: 12px;
        cursor: pointer;
        box-shadow: 0 3px 0 #333;
        transition: transform 0.1s, box-shadow 0.1s;
      }

      .tutorial-skip-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 5px 0 #333;
      }

      .tutorial-skip-btn:active {
        transform: translateY(2px);
        box-shadow: 0 1px 0 #333;
      }

      .tutorial-content {
        text-align: center;
      }

      .tutorial-icon {
        font-size: 48px;
        margin-bottom: 10px;
      }

      .tutorial-text {
        font-family: 'Comic Sans MS', cursive;
        font-size: 18px;
        color: #333;
        line-height: 1.4;
        margin-bottom: 10px;
      }

      .tutorial-tip {
        font-family: 'Comic Sans MS', cursive;
        font-size: 14px;
        color: #666;
        font-style: italic;
        display: none;
      }

      .tutorial-tip.visible {
        display: block;
      }

      .tutorial-footer {
        margin-top: 15px;
        text-align: center;
      }

      .tutorial-next-btn {
        background: linear-gradient(180deg, #4CAF50 0%, #388E3C 100%);
        color: white;
        border: 3px solid #333;
        border-radius: 15px;
        padding: 10px 30px;
        font-family: 'Comic Sans MS', cursive;
        font-size: 16px;
        font-weight: bold;
        cursor: pointer;
        box-shadow: 0 4px 0 #333;
        transition: transform 0.1s, box-shadow 0.1s;
      }

      .tutorial-next-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 0 #333;
      }

      .tutorial-next-btn:active {
        transform: translateY(2px);
        box-shadow: 0 2px 0 #333;
      }

      .tutorial-next-btn:disabled {
        background: #ccc;
        cursor: not-allowed;
      }

      .tutorial-pointer {
        position: absolute;
        width: 60px;
        height: 60px;
        background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23FFD700" stroke="%23333" stroke-width="2"><path d="M12 2L15 8L22 9L17 14L18 21L12 18L6 21L7 14L2 9L9 8Z"/></svg>');
        background-size: contain;
        display: none;
        animation: pointerPulse 1s ease-in-out infinite;
        pointer-events: none;
      }

      @keyframes pointerPulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.2); }
      }

      .tutorial-highlight {
        position: relative;
        z-index: 9999;
        animation: highlightPulse 1.5s ease-in-out infinite;
      }

      @keyframes highlightPulse {
        0%, 100% { box-shadow: 0 0 0 4px rgba(255, 215, 0, 0.5); }
        50% { box-shadow: 0 0 0 8px rgba(255, 215, 0, 0.8); }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(this.overlay);

    // Set up button listeners
    const skipBtn = this.overlay.querySelector('.tutorial-skip-btn');
    const nextBtn = this.overlay.querySelector('.tutorial-next-btn');

    skipBtn.addEventListener('click', () => this.skip());
    nextBtn.addEventListener('click', () => this.nextStep());
  }

  /**
   * Remove the tutorial overlay
   */
  removeOverlay() {
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }

    const style = document.getElementById('tutorial-styles');
    if (style) style.remove();

    // Remove any highlights
    document.querySelectorAll('.tutorial-highlight').forEach(el => {
      el.classList.remove('tutorial-highlight');
    });
  }

  /**
   * Show a tutorial step
   * @param {number} index - Step index
   */
  showStep(index) {
    if (!this.overlay || index >= TUTORIAL_STEPS.length) {
      this.complete();
      return;
    }

    const step = TUTORIAL_STEPS[index];
    this.currentStepIndex = index;

    // Update UI
    const stepIndicator = this.overlay.querySelector('.tutorial-step-indicator');
    const textEl = this.overlay.querySelector('.tutorial-text');
    const tipEl = this.overlay.querySelector('.tutorial-tip');
    const nextBtn = this.overlay.querySelector('.tutorial-next-btn');

    stepIndicator.textContent = `Step ${index + 1}/${TUTORIAL_STEPS.length}`;
    textEl.textContent = step.message;

    if (step.tip) {
      tipEl.textContent = `💡 ${step.tip}`;
      tipEl.classList.add('visible');
    } else {
      tipEl.classList.remove('visible');
    }

    // Handle highlighting
    this.clearHighlights();
    if (step.highlight) {
      this.highlightElement(step.highlight);
    }

    // Set up wait condition
    this.clearEventListeners();

    if (step.waitFor) {
      nextBtn.disabled = true;
      nextBtn.textContent = 'Waiting...';

      if (step.waitFor === 'near_pond') {
        // Special case: check player position
        this.checkNearPond();
      } else {
        // Wait for event
        const unsub = eventBus.once(step.waitFor, () => {
          nextBtn.disabled = false;
          nextBtn.textContent = 'Continue';
          // Auto-advance on certain events
          if (step.waitFor === GameEvents.FISH_BITING ||
              step.waitFor === GameEvents.MINIGAME_SUCCESS) {
            setTimeout(() => this.nextStep(), 500);
          }
        });
        this.unsubscribers.push(unsub);
      }
    } else {
      nextBtn.disabled = false;
      nextBtn.textContent = 'Continue';

      if (step.autoAdvance) {
        setTimeout(() => {
          if (this.currentStepIndex === index) {
            this.nextStep();
          }
        }, step.autoAdvance);
      }
    }

    // Check for completion
    if (step.onComplete) {
      this.markComplete();
    }
  }

  /**
   * Check if player is near a pond
   */
  checkNearPond() {
    // This would need to be integrated with the player position system
    // For now, we'll listen for fishing started as a proxy
    const unsub = eventBus.once(GameEvents.FISHING_STARTED, () => {
      this.nextStep();
    });
    this.unsubscribers.push(unsub);

    // Also enable manual continue after a delay
    setTimeout(() => {
      const nextBtn = this.overlay?.querySelector('.tutorial-next-btn');
      if (nextBtn) {
        nextBtn.disabled = false;
        nextBtn.textContent = 'Continue';
      }
    }, 5000);
  }

  /**
   * Highlight a UI element
   * @param {string} elementId - Element ID or type to highlight
   */
  highlightElement(elementId) {
    let element;

    switch (elementId) {
      case 'inventory-button':
        element = document.getElementById('inventory-button');
        break;
      case 'ground':
        // Can't highlight 3D element, show pointer instead
        break;
      case 'pond':
      case 'water':
      case 'float':
      case 'minigame':
        // 3D elements - show tip instead
        break;
      default:
        element = document.getElementById(elementId);
    }

    if (element) {
      element.classList.add('tutorial-highlight');
    }
  }

  /**
   * Clear all highlights
   */
  clearHighlights() {
    document.querySelectorAll('.tutorial-highlight').forEach(el => {
      el.classList.remove('tutorial-highlight');
    });
  }

  /**
   * Clear event listeners
   */
  clearEventListeners() {
    this.unsubscribers.forEach(unsub => {
      if (typeof unsub === 'function') unsub();
    });
    this.unsubscribers = [];
  }

  /**
   * Go to next step
   */
  nextStep() {
    this.showStep(this.currentStepIndex + 1);
  }

  /**
   * Skip the tutorial
   */
  skip() {
    this.isActive = false;
    this.markComplete();
    this.clearEventListeners();
    this.removeOverlay();

    eventBus.emit(GameEvents.TUTORIAL_SKIPPED);
    console.log('Tutorial skipped');
  }

  /**
   * Complete the tutorial
   */
  complete() {
    this.isActive = false;
    this.markComplete();
    this.clearEventListeners();
    this.removeOverlay();

    eventBus.emit(GameEvents.TUTORIAL_COMPLETED);
    console.log('Tutorial completed');
  }

  /**
   * Reset tutorial (for testing)
   */
  reset() {
    try {
      localStorage.removeItem(STORAGE_KEYS.TUTORIAL_COMPLETE);
      this.completed = false;
    } catch (error) {
      console.warn('Failed to reset tutorial:', error);
    }
  }
}

// Singleton instance
export const tutorialSystem = new TutorialSystem();

export { TUTORIAL_STEPS };
export default TutorialSystem;
